<template>
    <div ref="mapWrapperRef" class="relative rounded-lg border border-gold overflow-hidden">
        <l-map ref="mapRef" style="height: 670px;" class="w-full" :zoom="6" :center="[46.603354, 1.888334]"
            :maxBounds="[[41, -6], [52.5, 10]]" :minZoom="mapMinZoom" :maxZoom="13" :options="mapOptions">
            <!-- Fond -->


            <!-- Vue régions -->
            <l-geo-json v-if="currentLevel === 'regions'" ref="geoJsonRef" :geojson="regionsMainland"
                :l-options-style="getRegionStyle" :options="geoJsonOptionsRegions" />

            <!-- Vue départements -->
            <l-geo-json v-if="currentLevel === 'departements'" ref="depGeoJsonRef" :geojson="departementsGeojson"
                :l-options-style="getRegionStyle" :options="geoJsonOptionsDeps" />

            <!-- Vue communes -->
            <l-geo-json v-if="currentLevel === 'communes'" ref="comGeoJsonRef" :geojson="communesGeojson"
                :l-options-style="getRegionStyle" :options="geoJsonOptionsComs" />

            <!-- Marqueurs des utilisateurs du top 10 (adapte l'affichage selon le niveau) -->
            <TopUserMarkers v-if="topUsers.length > 0 && !isTransitioning" :top-users="topUsers"
                :current-level="currentLevel" :communes-geo-json="communesGeojson"
                :hovered-username-prop="hoveredUsername" :leaflet-map="leafletMap"
                @user-hover="(username) => emit('user-hover', username)" />
        </l-map>

        <!-- Encadré Corse (inset) -->
        <div v-if="currentLevel === 'regions'"
            class="map-overlay absolute bottom-3 right-3 w-[112px] h-[132px] sm:w-[142px] sm:h-[162px] rounded-md border border-gold shadow-glow-gold overflow-hidden bg-background/95">
            <div
                class="absolute top-1 left-1 z-30 text-[10px] px-1.5 py-0.5 rounded bg-primary/85 text-gold font-semibold">
                Corse
            </div>
            <l-map ref="corsicaMapRef" class="w-full h-full" :zoom="8" :center="[42.15, 9.05]" :minZoom="5"
                :maxZoom="11" :options="corsicaMapOptions">
                <l-geo-json ref="corsicaGeoJsonRef" :geojson="corsicaGeoJson" :l-options-style="getRegionStyle"
                    :options="corsicaGeoJsonOptions" />
            </l-map>
        </div>

        <!-- Bouton retour -->
        <div class="map-overlay absolute top-3 left-3 flex gap-2">
            <button v-if="currentLevel === 'departements'" @click="resetViewRegions"
                class="bg-gold text-primary px-3 py-1 rounded shadow-glow-gold hover:bg-gold/80 transition">
                Retour France
            </button>
            <button v-if="currentLevel === 'communes'" @click="resetViewDepartments"
                class="bg-gold text-primary px-3 py-1 rounded shadow-glow-gold hover:bg-gold/80 transition">
                Retour Départements
            </button>
        </div>

        <!-- Légende -->
        <div
            class="map-overlay absolute bottom-3 left-3 right-[145px] sm:right-auto bg-background/90 text-primary px-3 py-2 rounded shadow-glow-gold">
            <p class="text-xs sm:text-sm leading-snug">
                Survole une région — clique pour approfondir.
            </p>
        </div>

        <!-- Info utilisateurs top 10 -->
        <div
            class="map-overlay absolute bg-background/90 text-primary px-3 py-2 rounded shadow-glow-gold right-3 top-14 sm:top-3 max-w-[calc(100%-1.5rem)] sm:max-w-xs">
            <p class="text-xs italic opacity-80">
                {{ topUsersMessage }}
            </p>
        </div>

    </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { nextTick } from 'vue'
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { LMap, LGeoJson } from '@vue-leaflet/vue-leaflet'
import 'leaflet/dist/leaflet.css'

import regions from './layers/regions/regions_version_gregoiredavid.json'
import { getRegionStyle, getHoverStyle } from './utils/mapStyles'
import TopUserMarkers from './TopUserMarkers.vue'

/**
 * Props du composant
 */
// eslint-disable-next-line no-unused-vars
const props = defineProps({
    topUsers: {
        type: Array,
        default: () => [],
        required: false,
    },
    hoveredUsername: {
        type: String,
        default: null,
        required: false,
    },
});

const emit = defineEmits([
    'region-selected',
    'department-selected',
    'commune-selected',
    'user-hover'
])

const mapRef = ref(null)
const mapWrapperRef = ref(null)
const corsicaMapRef = ref(null)
const corsicaGeoJsonRef = ref(null)
const geoJsonRef = ref(null)
const depGeoJsonRef = ref(null)
const comGeoJsonRef = ref(null)

const leafletMap = computed(() => mapRef.value?.leafletObject || null)

const currentLevel = ref('regions')
const currentRegion = ref(null)
const departementsGeojson = ref(null)
const communesGeojson = ref(null)
const mapMinZoom = ref(6)
const isTransitioning = ref(false) // Indicateur de transition en cours
let franceBounds = null
let resizeObserver = null
let resizeRaf = null
let initialLayoutTimeouts = []

const mapOptions = {
    zoomControl: false,
    attributionControl: false,
    scrollWheelZoom: true,
    dragging: true,
    doubleClickZoom: true,
    boxZoom: true,
    touchZoom: true,
}

const regionsMainland = {
    type: 'FeatureCollection',
    features: (regions.features || []).filter((feature) => feature?.properties?.code !== '94')
}

const corsicaGeoJson = {
    type: 'FeatureCollection',
    features: (regions.features || []).filter((feature) => feature?.properties?.code === '94')
}

const corsicaMapOptions = {
    zoomControl: false,
    attributionControl: false,
    scrollWheelZoom: false,
    dragging: false,
    doubleClickZoom: false,
    boxZoom: false,
    touchZoom: false,
}

const corsicaGeoJsonOptions = {
    onEachFeature: (feature, layer) => {
        const initialStyle = getRegionStyle(feature)
        layer.setStyle(initialStyle)
        layer.options._initialStyle = initialStyle

        layer.on('mouseover', () => layer.setStyle(getHoverStyle(feature)))
        layer.on('mouseout', () => layer.setStyle(layer.options._initialStyle))

        layer.on('click', async () => {
            if (isTransitioning.value) return

            const regionCode = feature?.properties?.code
            const regionName = feature?.properties?.nom || 'Corse'
            const bounds = layer.getBounds?.() || null

            await drillIntoRegion(regionCode, regionName, bounds)
        })
    }
}

// Message adapté au niveau de zoom pour indiquer quels utilisateurs sont affichés
const topUsersMessage = computed(() => {
    switch (currentLevel.value) {
        case 'regions':
            return 'Seuls les utilisateurs du top 10 ayant renseigné leur région apparaissent sur la carte';
        case 'departements':
            return 'Seuls les utilisateurs du top 10 ayant renseigné leur département apparaissent sur la carte';
        case 'communes':
            return 'Seuls les utilisateurs du top 10 ayant renseigné leur commune apparaissent sur la carte';
        default:
            return '';
    }
});

onMounted(() => {
    const map = mapRef.value?.leafletObject
    const geoJsonLayer = geoJsonRef.value?.leafletObject
    if (map && geoJsonLayer) {
        franceBounds = geoJsonLayer.getBounds()
        map.fitBounds(franceBounds)
    }

    setupMapResizeHandling()
    runInitialLayoutCalibration()
})

onBeforeUnmount(() => {
    teardownMapResizeHandling()
})

function invalidateMapSize() {
    const map = mapRef.value?.leafletObject
    if (!map) return

    if (resizeRaf) {
        cancelAnimationFrame(resizeRaf)
    }

    resizeRaf = requestAnimationFrame(() => {
        map.invalidateSize({ pan: false, animate: false })
        applyResponsiveViewport()
        fitCorsicaInset()
    })
}

function fitCorsicaInset() {
    const insetMap = corsicaMapRef.value?.leafletObject
    const insetLayer = corsicaGeoJsonRef.value?.leafletObject
    if (!insetMap || !insetLayer) return

    insetMap.invalidateSize({ pan: false, animate: false })
    const bounds = insetLayer.getBounds?.()
    if (bounds && bounds.isValid && bounds.isValid()) {
        insetMap.fitBounds(bounds, { padding: [6, 6], maxZoom: 8, animate: false })
    }
}

function getResponsiveMinZoom() {
    const width = window.innerWidth || 1024

    if (width < 480) return 4.9
    if (width < 768) return 5.2
    if (width < 1024) return 5.6
    return 6
}

function getCurrentBounds() {
    if (currentLevel.value === 'communes') {
        return comGeoJsonRef.value?.leafletObject?.getBounds?.() || null
    }

    if (currentLevel.value === 'departements') {
        return depGeoJsonRef.value?.leafletObject?.getBounds?.() || null
    }

    return geoJsonRef.value?.leafletObject?.getBounds?.() || franceBounds
}

function applyResponsiveViewport() {
    const map = mapRef.value?.leafletObject
    if (!map) return

    const nextMinZoom = getResponsiveMinZoom()
    mapMinZoom.value = nextMinZoom
    map.setMinZoom(nextMinZoom)

    const currentBounds = getCurrentBounds()
    if (currentBounds && currentBounds.isValid && currentBounds.isValid()) {
        map.fitBounds(currentBounds, { padding: [16, 16], animate: false })
    }
}

function setupMapResizeHandling() {
    window.addEventListener('resize', invalidateMapSize)
    window.addEventListener('orientationchange', invalidateMapSize)
    window.addEventListener('pageshow', invalidateMapSize)

    if (typeof ResizeObserver !== 'undefined' && mapWrapperRef.value) {
        resizeObserver = new ResizeObserver(() => {
            invalidateMapSize()
        })
        resizeObserver.observe(mapWrapperRef.value)
    }

    // Cas où la page finit de charger après le montage Vue.
    window.addEventListener('load', invalidateMapSize, { once: true })
}

function runInitialLayoutCalibration() {
    invalidateMapSize()

    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            invalidateMapSize()
        })
    })

    initialLayoutTimeouts = [120, 300, 700].map((delay) => setTimeout(() => {
        invalidateMapSize()
    }, delay))
}

function teardownMapResizeHandling() {
    window.removeEventListener('resize', invalidateMapSize)
    window.removeEventListener('orientationchange', invalidateMapSize)
    window.removeEventListener('pageshow', invalidateMapSize)
    window.removeEventListener('load', invalidateMapSize)

    if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
    }

    if (resizeRaf) {
        cancelAnimationFrame(resizeRaf)
        resizeRaf = null
    }

    if (initialLayoutTimeouts.length) {
        initialLayoutTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
        initialLayoutTimeouts = []
    }
}

const resetViewRegions = async () => {
    isTransitioning.value = true // Masquer les marqueurs pendant la transition

    await nextTick()
    currentLevel.value = 'regions'
    departementsGeojson.value = null

    await nextTick() // attendre le DOM + composants réaffichés
    const map = mapRef.value?.leafletObject
    const geoJsonLayer = geoJsonRef.value?.leafletObject
    if (map && geoJsonLayer) {
        const bounds = geoJsonLayer.getBounds()
        franceBounds = bounds
        map.fitBounds(bounds)
    }

    // L'encart Corse est remonté en vue régions: on réinitialise son cadrage.
    setTimeout(() => {
        fitCorsicaInset()
    }, 0)

    emit('region-selected', null)

    // Réafficher les marqueurs après un court délai
    setTimeout(() => {
        isTransitioning.value = false
    }, 100)
}

const resetViewDepartments = async () => {
    isTransitioning.value = true // Masquer les marqueurs pendant la transition

    await nextTick()
    currentLevel.value = 'departements'
    communesGeojson.value = null

    await nextTick() // attendre le DOM + composants réaffichés
    const map = mapRef.value?.leafletObject
    const geoJsonLayer = depGeoJsonRef.value?.leafletObject
    if (map && geoJsonLayer) {
        const bounds = geoJsonLayer.getBounds()
        franceBounds = bounds
        map.fitBounds(bounds)
    }

    emit('region-selected', currentRegion.value)

    // Réafficher les marqueurs après un court délai
    setTimeout(() => {
        isTransitioning.value = false
    }, 100)
}

const geoJsonOptionsRegions = {
    onEachFeature: (feature, layer) => {
        const initialStyle = getRegionStyle(feature)
        layer.setStyle(initialStyle)
        layer.options._initialStyle = initialStyle

        const nom = feature?.properties?.nom || 'Région'
        layer.bindTooltip(nom, { sticky: true, direction: 'auto', offset: [8, 0] })

        layer.on('mouseover', () => layer.setStyle(getHoverStyle(feature)))
        layer.on('mouseout', () => layer.setStyle(layer.options._initialStyle))

        layer.on('click', async (e) => {
            const regionCode = feature?.properties?.code
            const regionName = feature?.properties?.nom || 'Région'
            await drillIntoRegion(regionCode, regionName, e.target.getBounds())
        })
    }
}

async function drillIntoRegion(regionCode, regionName, bounds = null) {
    const map = mapRef.value?.leafletObject
    if (!map || isTransitioning.value || !regionCode) return

    isTransitioning.value = true

    if (bounds && bounds.isValid && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [20, 20] })
    }

    currentRegion.value = {
        id: regionCode,
        name: regionName
    }

    emit('region-selected', { id: regionCode, name: regionName })

    try {
        await nextTick()
        const depData = await import(`./layers/departements/${regionCode}.json`)
        departementsGeojson.value = depData.default
        currentLevel.value = 'departements'
        await nextTick()
        setTimeout(() => {
            isTransitioning.value = false
        }, 100)
    } catch (err) {
        console.error(`Pas de données départements pour ${regionCode}`, err)
        isTransitioning.value = false
    }
}

const geoJsonOptionsDeps = {
    onEachFeature: (feature, layer) => {
        const initialStyle = getRegionStyle(feature)
        layer.setStyle(initialStyle)
        layer.options._initialStyle = initialStyle

        const nom = feature?.properties?.nom || 'Département'
        layer.bindTooltip(nom, { sticky: true, direction: 'auto', offset: [8, 0] })

        layer.on('mouseover', () => layer.setStyle(getHoverStyle(feature)))
        layer.on('mouseout', () => layer.setStyle(layer.options._initialStyle))

        layer.on('click', async (e) => {
            const map = mapRef.value?.leafletObject
            if (!map || isTransitioning.value) return // Ignorer les clics pendant les transitions

            isTransitioning.value = true
            map.fitBounds(e.target.getBounds(), { padding: [20, 20] })

            // Chargement dynamique des communes
            const departementCode = feature.properties.code
            const departementName = feature.properties.nom

            emit('department-selected', { id: departementCode, name: departementName })
            try {
                await nextTick()
                const comData = await import(`./layers/communes/${departementCode}.json`)
                communesGeojson.value = comData.default
                currentLevel.value = 'communes'
                await nextTick()
                setTimeout(() => {
                    isTransitioning.value = false
                }, 100)
            } catch (err) {
                console.error(`Pas de données communes pour ${departementCode}`, err)
                isTransitioning.value = false
            }
        })
    }
}

const geoJsonOptionsComs = {
    onEachFeature: (feature, layer) => {
        const initialStyle = getRegionStyle(feature)
        layer.setStyle(initialStyle)
        layer.options._initialStyle = initialStyle

        const nom = feature?.properties?.nom || 'Commune'
        const code = feature?.properties?.code // ou insee selon ton geojson

        layer.bindTooltip(nom, {
            sticky: true,
            direction: 'auto',
            offset: [8, 0]
        })

        layer.on('mouseover', () => {
            layer.setStyle(getHoverStyle(feature))

            emit('commune-hover', {
                id: code,
                name: nom
            })
        })

        layer.on('mouseout', () => {
            layer.setStyle(layer.options._initialStyle)

            emit('commune-hover-out')
        })

        // OPTIONNEL : si tu veux aussi le clic
        layer.on('click', () => {
            emit('commune-selected', {
                id: code,
                name: nom
            })
        })
    }
}

</script>

<style scoped>
.leaflet-container {
    background: #FAEBD7;
}

.map-overlay {
    z-index: 1200;
}
</style>
